import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';

import Button from '../../../widgets/Button';
import SegmentedControl from '../../SegmentedControl';
import DateRangePickerModal from './DateRangePickerModal';

import { useWidgetsModalContext } from '../../../../shared/providers/WidgetsModalProvider';
import useFilters from '../../../../utilities/hooks/useFilters';
import { IDateRange } from '../../../../shared/types/filters';
import {
  SpecialDateFilterValue,
  DateFilterOptions,
} from '../../../../shared/constants/HeadingFilter';

import { useDeviceType } from '../../../../utilities/common/deviceType';
import {
  formatDateToDisplayLabel,
  getDateFilterWindow,
  isSpecialDateFilterValue,
} from '../../../../utilities/common/date';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import { ISO_DATE_FORMAT } from '../../../../shared/constants/date';

interface IDatePickerProps {
  isMobileSheetVisible: boolean;
  setMobileSheetOpen?: (open: boolean) => void;
  dataLastUpdatedDate: string;
}

// Date filter preset values used across different route types
const DateFilterPreset = {
  TWENTY_FOUR_MONTHS: 24,
  THREE_MONTHS: 3,
  CUSTOM_DATE: SpecialDateFilterValue.CUSTOM_DATE,
  ALL_TIME: SpecialDateFilterValue.ALL_TIME,
} as const;

function DatePresetsPicker({
  isMobileSheetVisible,
  setMobileSheetOpen,
  dataLastUpdatedDate,
}: IDatePickerProps) {
  const { showDatePicker, setShowDatePicker } = useWidgetsModalContext();
  const isMobile = useDeviceType();
  const { t } = useTranslation();
  const { slug } = useParams();
  const { configData } = useFilterData();

  const { dateRange, setDateRange, selectedDate, setSelectedDate } =
    useFilters();

  /*
   * Local state to manage date picker selection before applying
   * will only update global dateRange state on 'apply' click to trigger a refetch with new dates
   */
  const [datePickerSelectedRange, setDatePickerSelectedRange] =
    useState<IDateRange>(dateRange);

  const fromDate = new Date(datePickerSelectedRange.fromDate);
  const toDate = new Date(datePickerSelectedRange.toDate);

  const isDimensionsRoute = slug?.startsWith('dimensions');

  // quarter filter is applicable for only dimensions route right now
  const isQuarterFilter = isDimensionsRoute;

  const getAllowedDateFilterOptions = (
    isDimensionsRoute: boolean | undefined,
  ) =>
    // dimensions have quarterly date filter
    // others have monthly date filter
    DateFilterOptions.filter(
      (filter) =>
        filter.value === DateFilterPreset.ALL_TIME ||
        (isDimensionsRoute ? filter.isOrgDim2Filter : !filter.isOrgDim2Filter),
    );

  const allowedFilterOptions = getAllowedDateFilterOptions(isDimensionsRoute);

  const getCompatibleFilterValue = (value: number) => {
    // Ensures the selected filter is valid for the current route; falls back to ALL_TIME.
    // for example, quarterly filters aren't valid for non-dimensions routes.
    const allowedDateFilterValues = new Set(
      allowedFilterOptions.map((filter) => filter.value),
    );

    return allowedDateFilterValues.has(value)
      ? value
      : DateFilterPreset.ALL_TIME;
  };

  // Calculates date range based on filter value and constraints
  const calculateDateRange = (filterValue: number) => {
    return getDateFilterWindow({
      monthOffset: filterValue,
      contractDate: configData?.contractStartDate,
      isQuarterFilter,
      filterAvailableTill: dataLastUpdatedDate,
    });
  };

  // Applies a date filter: updates selection state and date range if not in mobile sheet mode
  const applyDateFilter = (filterValue: number) => {
    const selectedFilter = DateFilterOptions.find(
      (filter) => filter.value === filterValue,
    );

    if (!selectedFilter) return;

    const isCustomDate = filterValue === SpecialDateFilterValue.CUSTOM_DATE;

    // Update selected filter state (except for custom date which is handled separately)
    if (!isCustomDate) {
      setSelectedDate(selectedFilter);
    }

    // Apply date range immediately on desktop; mobile waits for "Apply" button
    if (!isCustomDate && !isMobileSheetVisible) {
      const dateRange = calculateDateRange(filterValue);
      setDateRange(dateRange);
    }
  };

  // Reset filter to compatible value when route changes
  useEffect(() => {
    const compatibleValue = getCompatibleFilterValue(selectedDate.value);
    applyDateFilter(compatibleValue);
  }, [slug]);

  // Sync local date picker selection with global date range when date range changes externally (e.g. route change or preset filter applied)
  useEffect(() => {
    setDatePickerSelectedRange(dateRange);
  }, [dateRange]);

  // Applies date range from mobile bottom sheet when user clicks "Apply"
  const applyMobileSheetSelection = () => {
    const calculatedRange = calculateDateRange(selectedDate.value);

    // For quarter filters, use calculated range; for custom dates, use the picker selection
    const finalRange = isQuarterFilter
      ? calculatedRange
      : {
          fromDate: format(fromDate, ISO_DATE_FORMAT),
          toDate: format(toDate, ISO_DATE_FORMAT),
        };

    setDateRange(finalRange);
    setMobileSheetOpen?.(false);
  };

  // Applies custom date range from date picker modal when user clicks "Apply"
  const applyCustomDateRange = () => {
    setSelectedDate({
      label: 'CUSTOM_DATE',
      value: SpecialDateFilterValue.CUSTOM_DATE,
    });
    setDateRange(datePickerSelectedRange);
    setShowDatePicker(false);
  };

  const getSegmentsDisplayLabel = (
    filter: (typeof DateFilterOptions)[number],
    isSelected: boolean,
  ): string => {
    if (isSelected && filter.value === SpecialDateFilterValue.CUSTOM_DATE) {
      return `${formatDateToDisplayLabel(dateRange.fromDate)} - ${formatDateToDisplayLabel(dateRange.toDate)}`;
    }

    return t(filter.label);
  };

  return (
    <>
      <div className={isMobileSheetVisible ? 'pb-[100px]' : ''}>
        <SegmentedControl
          options={allowedFilterOptions}
          selectedValue={selectedDate.value}
          onChange={(value) => applyDateFilter(Number(value))}
          onOptionClick={(value) => {
            if (isMobile) {
              setMobileSheetOpen?.(true);
            }
            if (Number(value) === SpecialDateFilterValue.CUSTOM_DATE) {
              setShowDatePicker(true);
            }
          }}
          renderLabel={(filter, isSelected) => {
            const dateFilter = filter as (typeof DateFilterOptions)[number];
            const displayLabel = getSegmentsDisplayLabel(
              dateFilter,
              isSelected,
            );

            if (isSpecialDateFilterValue(filter.value)) {
              return displayLabel;
            }

            const timePeriod = dateFilter.isOrgDim2Filter
              ? t('QUARTERS')
              : t('MONTHS');

            return `${t('LAST')} ${displayLabel} ${timePeriod}`;
          }}
          getOptionClassName={(_, isSelected) => {
            const shouldHide = isMobile && !isSelected;
            return shouldHide ? 'hidden' : '';
          }}
          mobileSheet={isMobileSheetVisible}
        />

        {isMobileSheetVisible && (
          <div className="border-taupe-600 absolute bottom-0 left-0 m-auto flex w-full justify-center border-t border-solid py-6">
            <Button
              className="w-full max-w-[90%]"
              onClick={applyMobileSheetSelection}
            >
              {t('APPLY')}
            </Button>
          </div>
        )}
      </div>

      <DateRangePickerModal
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        dateRange={datePickerSelectedRange}
        onDateRangeChange={setDatePickerSelectedRange}
        onApply={applyCustomDateRange}
        maxDate={dataLastUpdatedDate}
      />
    </>
  );
}

export default DatePresetsPicker;
